import { appProfiler } from './appProfiler';

export interface DataPoint {
  isRealCandle?: boolean;
  time: number;
  price: number;
  momentum: number;
  trend?: number;
  imf?: number;
  volume?: number;
  timestamp?: number;
  closeTime?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  sma10?: number;
  sma20?: number;
  sma50?: number;
  sma10Momentum?: number;
  sma20Momentum?: number;
  sma50Momentum?: number;
  smaMomentum?: number;
  sma10Imf?: number;
  sma20Imf?: number;
  sma50Imf?: number;
  smaImf?: number;
  tangent?: [number, number, number];
  macroTangent?: [number, number, number];
  resonance?: number;
}

export interface StandardCandle {
    timestamp: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    closeTime?: number;
}

export function normalizeBinanceToCandle(rawBinanceData: any[]): StandardCandle[] {
    return rawBinanceData.map(d => ({
        timestamp: d[0],
        open: typeof d[1] === 'string' ? parseFloat(d[1]) : d[1],
        high: typeof d[2] === 'string' ? parseFloat(d[2]) : d[2],
        low: typeof d[3] === 'string' ? parseFloat(d[3]) : d[3],
        close: typeof d[4] === 'string' ? parseFloat(d[4]) : d[4],
        volume: typeof d[5] === 'string' ? parseFloat(d[5]) : d[5],
        closeTime: d[6]
    }));
}

// ---------------------------------------------------------------------------
// Hilbert / FFT in-place (Cooley-Tukey Radix-2 iterativo)
//
// Sem recursão, sem .filter() e sem objetos { re, im }: os dados vivem em
// Float64Array pré-alocados e REUTILIZADOS entre chamadas (os buffers só crescem
// quando chega um sinal maior). Assim o cálculo não gera lixo para o Garbage
// Collector e não causa pausas durante o arrasto/atualização do gráfico.
// ---------------------------------------------------------------------------
interface FftPlan {
    n: number;
    cos: Float64Array;      // cos(-2πk/n), k < n/2
    sin: Float64Array;      // sin(-2πk/n), k < n/2
    bitrev: Uint32Array;    // permutação de bits (reversão)
}

const fftPlans = new Map<number, FftPlan>();
let fftRe = new Float64Array(0);
let fftIm = new Float64Array(0);

function getFftPlan(n: number): FftPlan {
    let plan = fftPlans.get(n);
    if (plan) return plan;
    const half = n >> 1;
    const cos = new Float64Array(half);
    const sin = new Float64Array(half);
    for (let k = 0; k < half; k++) {
        const angle = (-2 * Math.PI * k) / n;
        cos[k] = Math.cos(angle);
        sin[k] = Math.sin(angle);
    }
    const bitrev = new Uint32Array(n);
    const bits = Math.round(Math.log2(n));
    for (let i = 0; i < n; i++) {
        let x = i, r = 0;
        for (let b = 0; b < bits; b++) { r = (r << 1) | (x & 1); x >>= 1; }
        bitrev[i] = r;
    }
    plan = { n, cos, sin, bitrev };
    fftPlans.set(n, plan);
    return plan;
}

/** FFT direta in-place sobre (re, im), tamanho n = potência de 2. */
function fftInPlace(re: Float64Array, im: Float64Array, plan: FftPlan): void {
    const n = plan.n;
    const { cos, sin, bitrev } = plan;
    for (let i = 0; i < n; i++) {
        const j = bitrev[i];
        if (j > i) {
            const tr = re[i]; re[i] = re[j]; re[j] = tr;
            const ti = im[i]; im[i] = im[j]; im[j] = ti;
        }
    }
    for (let len = 2; len <= n; len <<= 1) {
        const halfLen = len >> 1;
        const step = n / len;
        for (let start = 0; start < n; start += len) {
            for (let k = 0, t = 0; k < halfLen; k++, t += step) {
                const wr = cos[t], wi = sin[t];
                const a = start + k, b = a + halfLen;
                const xr = re[b] * wr - im[b] * wi;
                const xi = re[b] * wi + im[b] * wr;
                re[b] = re[a] - xr; im[b] = im[a] - xi;
                re[a] += xr;        im[a] += xi;
            }
        }
    }
}

export class HilbertMath {
    static transform(signal: number[]): number[] {
        const signalN = signal.length;
        if (signalN === 0) return [];
        const N = Math.pow(2, Math.ceil(Math.log2(signalN)));
        // N < 4: só existem DC e Nyquist, que o filtro de Hilbert zera
        if (N < 4) return new Array(signalN).fill(0);

        if (fftRe.length < N) {
            fftRe = new Float64Array(N);
            fftIm = new Float64Array(N);
        }
        const re = fftRe, im = fftIm;
        const plan = getFftPlan(N);

        // 1. Preenche (com o último valor como padding até a potência de 2)
        const lastVal = signal[signalN - 1];
        for (let i = 0; i < signalN; i++) { re[i] = signal[i]; im[i] = 0; }
        for (let i = signalN; i < N; i++) { re[i] = lastVal; im[i] = 0; }

        // 2. FFT direta
        fftInPlace(re, im, plan);

        // 3. Multiplicador de Hilbert: frequências positivas x (-i), negativas x (+i); DC e Nyquist = 0
        const half = N >> 1;
        re[0] = 0; im[0] = 0;
        re[half] = 0; im[half] = 0;
        for (let k = 1; k < half; k++) {
            const a = re[k], b = im[k];
            re[k] = b; im[k] = -a;
        }
        for (let k = half + 1; k < N; k++) {
            const a = re[k], b = im[k];
            re[k] = -b; im[k] = a;
        }

        // 4. FFT inversa pelo truque do conjugado: conj -> FFT -> (parte real)/N
        for (let k = 0; k < N; k++) im[k] = -im[k];
        fftInPlace(re, im, plan);

        // 5. Parte real, só do tamanho original (única alocação: a própria saída)
        const out = new Array<number>(signalN);
        for (let i = 0; i < signalN; i++) out[i] = re[i] / N;
        return out;
    }
}

export class KalmanFilter {
    public q: number; // Process noise covariance
    public r: number; // Measurement noise covariance
    public x: number; // Value estimate
    public p: number; // Estimation error covariance
    public k: number; // Kalman gain

    constructor(q: number = 0.01, r: number = 0.1, p: number = 1.0, initialValue: number = 0) {
        this.q = q;
        this.r = r;
        this.p = p;
        this.x = initialValue;
        this.k = 0;
    }

    clone(): KalmanFilter {
        const copy = new KalmanFilter(this.q, this.r, this.p, this.x);
        copy.k = this.k;
        return copy;
    }

    update(measurement: number): number {
        // Prediction update
        this.p = this.p + this.q;

        // Measurement update
        this.k = this.p / (this.p + this.r);
        this.x = this.x + this.k * (measurement - this.x);
        this.p = (1 - this.k) * this.p;

        return this.x;
    }
}

export interface CachedMathState {
    firstTimestamp: number;
    lastTimestamp: number;
    secondToLastTimestamp: number;
    // Identidade da série: horários iguais não bastam (BTC e ETH no mesmo timeframe têm os mesmos horários).
    // A primeira vela e a penúltima (já fechada) não mudam entre ticks ao vivo da mesma série.
    firstOpen: number;
    firstClose: number;
    secondToLastOpen: number;
    secondToLastClose: number;
    secondToLastHigh: number;
    secondToLastLow: number;
    count: number;
    savedKalmanBase: KalmanFilter;
    savedKalmanMicro: KalmanFilter;
    savedKalmanSlow: KalmanFilter;
    historicalPoints: DataPoint[];
    std: number;
    gamma: number;
    timeScale: number;
    normalizedHistory: number[];
    hilbertHistory: number[];
}

let cachedMathState: CachedMathState | null = null;

/** O cache só vale para a MESMA série: mesmos horários e mesmo conteúdo nas velas que não mudam entre ticks. */
function cacheMatchesSeries(validData: StandardCandle[]): boolean {
    const C = cachedMathState;
    const N = validData.length;
    if (C === null || N !== C.count || N < 2) return false;
    const a = validData[0], b = validData[N - 2];
    return (
        a.timestamp === C.firstTimestamp &&
        b.timestamp === C.secondToLastTimestamp &&
        validData[N - 1].timestamp === C.lastTimestamp &&
        a.open === C.firstOpen && a.close === C.firstClose &&
        b.open === C.secondToLastOpen && b.close === C.secondToLastClose &&
        b.high === C.secondToLastHigh && b.low === C.secondToLastLow
    );
}

const softClip = (v: number) => {
    const absV = Math.abs(v);
    if (absV <= 2.5) return v;
    return Math.sign(v) * (2.5 + Math.log1p(absV - 2.5));
};

export function processMathData(candles: StandardCandle[], options?: { sma10Period?: number, sma20Period?: number, sma50Period?: number }): DataPoint[] {
    return appProfiler.time('Math:processMathData(Total)', () => {
    if (!candles || candles.length === 0) return [];
    
    // Filter out rows with 0 price or non-finite numbers which break normalization
    const validData = candles.filter((c: StandardCandle) => {
        const p = c.close;
        return !isNaN(p) && isFinite(p) && p > 0;
    });
    if (validData.length === 0) return [];
    
    const N = validData.length;
    const isLiveTick = cacheMatchesSeries(validData);

    // FAST-PATH: Incremental O(1) tick update for live intraday price movement
    let finalPoints: DataPoint[] = [];
    if (isLiveTick && cachedMathState) {
        const lastCandle = validData[N - 1];
        const lastPrice = lastCandle.close;
        const lastOpen = lastCandle.open;
        const lastHigh = lastCandle.high;
        const lastLow = lastCandle.low;
        const lastVol = lastCandle.volume;
        const lastTime = lastCandle.timestamp;
        const lastCloseTime = lastCandle.closeTime;

        // Restore Kalman state cloned from N-2
        const kBase = cachedMathState.savedKalmanBase.clone();
        const fPrice = kBase.update(lastPrice);

        const kMicro = cachedMathState.savedKalmanMicro.clone();
        const mPrice = kMicro.update(lastPrice);

        const kSlow = cachedMathState.savedKalmanSlow.clone();
        const trendLive = kSlow.update(fPrice);

        const imfMacroLive = fPrice - trendLive;
        const imfMicroLive = mPrice - fPrice;
        const combinedIMFLive = imfMacroLive * 1.0 + imfMicroLive * 1.5;

        const rawNormLive = combinedIMFLive / cachedMathState.std;
        const normLive = softClip(rawNormLive);
        const imfFinalLive = normLive * cachedMathState.std * cachedMathState.gamma;

        // Continuous quadrature differential projection on live moving tip
        const prevNorm = cachedMathState.normalizedHistory[N - 2] ?? normLive;
        const prevHilbert = cachedMathState.hilbertHistory[N - 2] ?? 0;
        const deltaNorm = normLive - prevNorm;
        
        const hilbertLiveRaw = prevHilbert + (deltaNorm * 1.5);
        const hilbertFinalLive = softClip(hilbertLiveRaw);
        const hilbertPriceLive = hilbertFinalLive * cachedMathState.std * cachedMathState.gamma;
        const momentumLive = -hilbertPriceLive;

        // Build result points by reusing cached historical points
        // NOTE: On the main thread, cachedMathState.historicalPoints is already warmup-dropped 
        // and time-rescaled (because it points to the previous worker 'result').
        const L = cachedMathState.historicalPoints.length;
        finalPoints = new Array(L);
        for (let i = 0; i < L - 1; i++) {
            finalPoints[i] = cachedMathState.historicalPoints[i];
        }

        finalPoints[L - 1] = {
            isRealCandle: true,
            time: 1.0, // After rescaling, the last point is always exactly 1.0
            price: lastPrice,
            trend: trendLive,
            imf: imfFinalLive,
            momentum: momentumLive,
            volume: lastVol,
            timestamp: lastTime,
            closeTime: lastCloseTime,
            open: lastOpen,
            high: lastHigh,
            low: lastLow,
            close: lastPrice,
        };
        }

    else {
    // FULL-PATH: Complete Hilbert FFT & Kalman pipeline (candle closed, history shifted, timeframe changed)
    const rawPrices = validData.map((c: StandardCandle) => c.close);
    const timeScale = 1.0;

    let savedKalmanBaseAtN2: KalmanFilter | null = null;
    let savedKalmanMicroAtN2: KalmanFilter | null = null;
    let savedKalmanSlowAtN2: KalmanFilter | null = null;

    const { filteredPrices, microPrices, trend } = appProfiler.time('Math:KalmanDenoising', () => {
        const baseKalman = new KalmanFilter(0.005 * timeScale, 1.5 / timeScale, 1.0, rawPrices[0]);
        const microKalman = new KalmanFilter(0.04 * timeScale, 0.4 / timeScale, 1.0, rawPrices[0]);
        const slowKalman = new KalmanFilter(0.0001 * timeScale, 8.0 / timeScale, 1.0, rawPrices[0]);

        const fPrices = new Array(rawPrices.length);
        const mPrices = new Array(rawPrices.length);
        const tr = new Array(rawPrices.length);

        for (let i = 0; i < rawPrices.length; i++) {
            if (i === rawPrices.length - 2) {
                savedKalmanBaseAtN2 = baseKalman.clone();
                savedKalmanMicroAtN2 = microKalman.clone();
                savedKalmanSlowAtN2 = slowKalman.clone();
            }
            const p = rawPrices[i];
            const fp = baseKalman.update(p);
            fPrices[i] = fp;
            mPrices[i] = microKalman.update(p);
            tr[i] = slowKalman.update(fp);
        }

        return { filteredPrices: fPrices, microPrices: mPrices, trend: tr };
    });

    const imfMacro = filteredPrices.map((p: number, i: number) => p - trend[i]);
    const imfMicro = microPrices.map((p: number, i: number) => p - filteredPrices[i]);
    const gamma = 1.0;
    const combinedIMF = imfMacro.map((m, i) => m * 1.0 + imfMicro[i] * 1.5);

    const mad = combinedIMF.reduce((acc, val) => acc + Math.abs(val), 0) / combinedIMF.length;
    const std = (mad * 1.2533) || 1;

    const raw_normalized = combinedIMF.map((v: number) => v / std);
    const normalized = raw_normalized.map(v => softClip(v));
    const imf_final = normalized.map(v => v * std * gamma);

    // Padding + Cosine Taper for periodic FFT edge smoothness
    const PADDING_SIZE = 100;
    const paddedNormalized: number[] = [];
    
    if (normalized.length > 2) {
        let firstVal = normalized[0];
        let secondVal = normalized[1];
        let slopeBegin = firstVal - secondVal; 
        const beginPad = [];
        let currentBegin = firstVal;
        for(let i=0; i<PADDING_SIZE; i++) {
             currentBegin += slopeBegin;
             slopeBegin *= 0.95;
             beginPad.unshift(currentBegin);
        }
        
        let lastVal = normalized[normalized.length - 1];
        let prevVal = normalized[normalized.length - 2];
        let slopeEnd = lastVal - prevVal;
        const endPad = [];
        let currentEnd = lastVal;
        for (let i = 0; i < PADDING_SIZE; i++) {
            currentEnd += slopeEnd;
            slopeEnd *= 0.95; 
            endPad.push(currentEnd);
        }
        
        paddedNormalized.push(...beginPad, ...normalized, ...endPad);
        
        for (let i = 0; i < PADDING_SIZE; i++) {
            const taper = 0.5 * (1 - Math.cos((i / PADDING_SIZE) * Math.PI));
            paddedNormalized[i] *= taper;
            paddedNormalized[paddedNormalized.length - 1 - i] *= taper;
        }
    } else {
        paddedNormalized.push(...normalized);
    }

    const hilbert_norm_padded = appProfiler.time('Math:HilbertFFT', () => HilbertMath.transform(paddedNormalized));
    
    const hilbert_norm = normalized.length > 2 
        ? hilbert_norm_padded.slice(PADDING_SIZE, PADDING_SIZE + normalized.length)
        : hilbert_norm_padded.slice(0, normalized.length);

    const hilbert_final = hilbert_norm.map(v => softClip(v));
    const hilbert = hilbert_final.map(v => v * std * gamma);

    const resultPoints: DataPoint[] = [];
    for (let i = 0; i < validData.length; i++) {
        const d = validData[i];
        const normalizedMom = -hilbert[i];
        
        resultPoints.push({
            isRealCandle: true,
            time: i / Math.max(1, validData.length - 1),
            price: d.close,
            trend: trend[i],
            imf: imf_final[i],
            momentum: normalizedMom,
            volume: d.volume,
            timestamp: d.timestamp,
            closeTime: d.closeTime,
            open: d.open, high: d.high, low: d.low, close: d.close
        });
    }

    // Cache computed history for high-speed live tick processing
    if (validData.length >= 2 && savedKalmanBaseAtN2 && savedKalmanMicroAtN2 && savedKalmanSlowAtN2) {
        cachedMathState = {
            firstTimestamp: validData[0].timestamp,
            lastTimestamp: validData[validData.length - 1].timestamp,
            secondToLastTimestamp: validData[validData.length - 2].timestamp,
            firstOpen: validData[0].open,
            firstClose: validData[0].close,
            secondToLastOpen: validData[validData.length - 2].open,
            secondToLastClose: validData[validData.length - 2].close,
            secondToLastHigh: validData[validData.length - 2].high,
            secondToLastLow: validData[validData.length - 2].low,
            count: validData.length,
            savedKalmanBase: savedKalmanBaseAtN2,
            savedKalmanMicro: savedKalmanMicroAtN2,
            savedKalmanSlow: savedKalmanSlowAtN2,
            historicalPoints: resultPoints,
            std,
            gamma,
            timeScale,
            normalizedHistory: normalized,
            hilbertHistory: hilbert_final
        };
    }

    finalPoints = resultPoints;
    const warmupLength = 50;
    if (finalPoints.length > warmupLength * 2) {
        finalPoints = finalPoints.slice(Math.floor(warmupLength));
        const minTime = finalPoints[0].time;
        const maxTime = finalPoints[finalPoints.length - 1].time;
        if (maxTime > minTime) {
            finalPoints = finalPoints.map(p => ({
                    ...p,
                    time: (p.time - minTime) / (maxTime - minTime)
                }));
        }
    }

    
    }

    // SMA Calculation - Moved here to match old App.tsx pipeline EXACTLY (calculates AFTER warmup drop)
    appProfiler.time('Math:processMathData(SMA)', () => {
        const sma10Period = options?.sma10Period || 10, sma20Period = options?.sma20Period || 20, sma50Period = options?.sma50Period || 50;
        let sum10 = 0, sumMom10 = 0, sumImf10 = 0;
        let sum20 = 0, sumMom20 = 0, sumImf20 = 0;
        let sum50 = 0, sumMom50 = 0, sumImf50 = 0;

        for (let i = 0; i < finalPoints.length; i++) {
            const val = finalPoints[i].close ?? finalPoints[i].price;
            const mom = finalPoints[i].momentum || 0;
            const imf = finalPoints[i].imf || 0;

            sum10 += val; sumMom10 += mom; sumImf10 += imf;
            sum20 += val; sumMom20 += mom; sumImf20 += imf;
            sum50 += val; sumMom50 += mom; sumImf50 += imf;

            if (i >= sma10Period) {
                const old = finalPoints[i - sma10Period];
                sum10 -= (old.close ?? old.price);
                sumMom10 -= (old.momentum || 0);
                sumImf10 -= (old.imf || 0);
            }
            if (i >= sma20Period) {
                const old = finalPoints[i - sma20Period];
                sum20 -= (old.close ?? old.price);
                sumMom20 -= (old.momentum || 0);
                sumImf20 -= (old.imf || 0);
            }
            if (i >= sma50Period) {
                const old = finalPoints[i - sma50Period];
                sum50 -= (old.close ?? old.price);
                sumMom50 -= (old.momentum || 0);
                sumImf50 -= (old.imf || 0);
            }

            const win10 = Math.min(i + 1, sma10Period);
            const win20 = Math.min(i + 1, sma20Period);
            const win50 = Math.min(i + 1, sma50Period);

            finalPoints[i].sma10 = sum10 / win10;
            finalPoints[i].sma10Momentum = sumMom10 / win10;
            finalPoints[i].sma10Imf = sumImf10 / win10;

            finalPoints[i].sma20 = sum20 / win20;
            finalPoints[i].sma20Momentum = sumMom20 / win20;
            finalPoints[i].sma20Imf = sumImf20 / win20;

            finalPoints[i].sma50 = sum50 / win50;
            finalPoints[i].sma50Momentum = sumMom50 / win50;
            finalPoints[i].sma50Imf = sumImf50 / win50;

            finalPoints[i].smaMomentum = finalPoints[i].sma10Momentum;
            finalPoints[i].smaImf = finalPoints[i].sma10Imf;
        }
    });

    return finalPoints;
    });
}

export function getMathState(): CachedMathState | null {
    return cachedMathState;
}

export function setMathState(newState: any) {
    if (!newState) {
        cachedMathState = null;
        return;
    }
    // Rehydrate Kalman filters
    const base = new KalmanFilter();
    Object.assign(base, newState.savedKalmanBase);
    
    const micro = new KalmanFilter();
    Object.assign(micro, newState.savedKalmanMicro);
    
    const slow = new KalmanFilter();
    Object.assign(slow, newState.savedKalmanSlow);
    
    cachedMathState = {
        ...newState,
        savedKalmanBase: base,
        savedKalmanMicro: micro,
        savedKalmanSlow: slow
    };
}

export function checkIsLiveTick(candles: StandardCandle[]): boolean {
    if (!candles || candles.length < 2) return false;
    const validData = candles.filter((c: StandardCandle) => {
        const p = c.close;
        return !isNaN(p) && isFinite(p) && p > 0;
    });
    return cacheMatchesSeries(validData);
}
